import type { ConfigurableProperty, DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingLevel } from 'api/digitalTwin/digitalTwin.types'
import { clone } from 'helpers/global.helper/global.helper'
import { applyAliases, generallyApplyAliases } from 'helpers/uiConfig.helper/uiConfig.helper'
import { getAliasesForAliasExpansion } from 'helpers/uiConfig.helper/uiConfigResolutionPipeline.helper'
import i18n from 'i18next'
import { getContrastingColors } from 'utils/colorUtils'
import { getPrettyName, getPropertyPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import type { AppliedSettingSeries } from './appliedSettingValues.types'

const PALETTE_ALIASES = Array.from({ length: 12 }, (_, index) => `$color-${index + 1}`)

/**
 * Which level wins when a BaseConfig item's name exists on several. An item's `setting_level` replaces it:
 * the item then joins only an object at that level, or none.
 */
const LEVEL_PRECEDENCE: DeviationSettingLevel[] = [
  'unit',
  'exchange',
  'balance',
  'commodity',
  'byproduct',
  'node',
  'model',
]

type SettingsObject = {
  level: DeviationSettingLevel
  name: string
  properties: ConfigurableProperty[]
}

type CatalogueObject = SettingsObject & { title: string; item: UiConfigPropsItem }

export function isUnitNameTranslated(unitName: string, translations: DigitalTwinTranslations): boolean {
  return getPrettyName(unitName, translations) !== unitName
}

function getJoinKey(item: UiConfigPropsItem): string | undefined {
  return item.data_id?.split('.').at(-1)
}

function hasOrder(item: UiConfigPropsItem): item is UiConfigPropsItem & { order: number } {
  return typeof item.order === 'number'
}

function sortByBaseConfigOrder(items: UiConfigPropsItem[]): UiConfigPropsItem[] {
  const ordered = items.filter(hasOrder).sort((a, b) => a.order - b.order)

  return [...ordered, ...items.filter((item) => !hasOrder(item))]
}

function groupPropertiesByObject(allProperties: ConfigurableProperty[]): SettingsObject[] {
  const objects = new Map<string, SettingsObject>()
  allProperties.forEach((property) => {
    const key = JSON.stringify([property.level, property.name])
    const object = objects.get(key) ?? { level: property.level, name: property.name, properties: [] }
    objects.set(key, { ...object, properties: [...object.properties, property] })
  })

  return [...objects.values()]
}

function findObjectForItem(item: UiConfigPropsItem, objects: SettingsObject[]): SettingsObject | undefined {
  const name = getJoinKey(item)
  const sameName = objects.filter((object) => object.name === name)
  if (item.setting_level !== undefined) {
    return sameName.find((object) => object.level === item.setting_level)
  }

  return LEVEL_PRECEDENCE.map((level) => sameName.find((object) => object.level === level)).find(Boolean)
}

/**
 * Picks the one item an object is joined to when several BaseConfig items share its name: the first with
 * a non-negative or no `order`, otherwise the first.
 */
function pickObjectItem(candidates: UiConfigPropsItem[]): UiConfigPropsItem {
  return candidates.find((item) => !hasOrder(item) || item.order >= 0) ?? candidates[0]
}

/**
 * Pairs each digital-twin object that has settings with its BaseConfig item, in BaseConfig order, and
 * gives it a title. Items that match no object are left out.
 */
function joinItemsToObjects(
  sortedItems: UiConfigPropsItem[],
  objects: SettingsObject[],
  translations: DigitalTwinTranslations
): CatalogueObject[] {
  const candidatesByObject = new Map<SettingsObject, UiConfigPropsItem[]>()
  sortedItems.forEach((item) => {
    const object = findObjectForItem(item, objects)
    if (object) {
      candidatesByObject.set(object, [...(candidatesByObject.get(object) ?? []), item])
    }
  })

  const joinedObjects = new Map(
    [...candidatesByObject].map(([object, candidates]) => [pickObjectItem(candidates), object] as const)
  )

  return sortedItems.flatMap((item) => {
    const object = joinedObjects.get(item)
    if (object === undefined) {
      return []
    }

    const title = isUnitNameTranslated(object.name, translations)
      ? getPrettyName(object.name, translations)
      : item.title || object.name

    return [{ ...object, title, item }]
  })
}

/**
 * A setting's return id, from the property it shows rather than its position, so an id never re-points to
 * another property when the catalogue is rebuilt. Hex keeps it within the backend's return-id alphabet.
 */
function toSettingReturnId({ level, name, attribute }: ConfigurableProperty): string {
  const identity = new TextEncoder().encode(JSON.stringify([level, name, attribute]))

  return `setting_${Array.from(identity, (byte) => byte.toString(16).padStart(2, '0')).join('')}`
}

function toSettingSeries(
  property: ConfigurableProperty,
  owner: { level: DeviationSettingLevel; name: string; title: string },
  color: string,
  translations: DigitalTwinTranslations
): AppliedSettingSeries {
  const propertyTitle = getPropertyPrettyName(property.name, property.attribute, translations)

  return {
    kind: 'setting',
    property,
    returnId: toSettingReturnId(property),
    level: owner.level,
    unitName: owner.name,
    unitTitle: owner.title,
    propertyTitle,
    displayTitle: `${owner.title}: ${propertyTitle}`,
    color,
    measurementUnit: property.measurement_unit,
  }
}

/** An object of any level with no BaseConfig item has no outcome or optimal: one series per setting. */
function toSettingsOnlySeries(
  object: SettingsObject,
  palette: string[],
  translations: DigitalTwinTranslations
): AppliedSettingSeries[] {
  const owner = { level: object.level, name: object.name, title: getPrettyName(object.name, translations) }
  const colors = getContrastingColors(undefined, 1 + object.properties.length, palette)

  return object.properties.map((property, propertyIndex) =>
    toSettingSeries(property, owner, colors[1 + propertyIndex], translations)
  )
}

/**
 * One object's series: its outcome, its optimal, then one per setting. Outcome keeps the item colour;
 * the others take the palette colours that contrast most with the object's other series.
 */
function toObjectSeries(
  object: CatalogueObject,
  palette: string[],
  translations: DigitalTwinTranslations
): AppliedSettingSeries[] {
  const colors = getContrastingColors(object.item.color, 2 + object.properties.length, palette)
  const seriesFields = (slot: number, propertyTitle: string) => ({
    level: object.level,
    unitName: object.name,
    unitTitle: object.title,
    propertyTitle,
    displayTitle: `${object.title}: ${propertyTitle}`,
    color: colors[slot],
  })

  return [
    {
      ...seriesFields(0, i18n.t('Outcome')),
      kind: 'outcome',
      returnId: `outcome_${object.item.data_id}`,
      measurementUnit: object.item.unit,
    },
    {
      ...seriesFields(1, i18n.t('Optimal')),
      kind: 'optimal',
      returnId: `optimal_${object.item.data_id}`,
      measurementUnit: object.item.unit,
    },
    ...object.properties.map((property, propertyIndex) =>
      toSettingSeries(
        property,
        { level: object.level, name: object.name, title: object.title },
        colors[2 + propertyIndex],
        translations
      )
    ),
  ]
}

/**
 * Builds every series Applied setting values can show, for both its graph and its table, before any
 * filtering. Objects of any level come from BaseConfig items joined to the digital twin's settings by
 * level and name; each yields its outcome, its optimal, and one series per setting. An object no item
 * joins, of any level, follows as settings only. Every series carries the return id its data is fetched
 * under, its titles, its measurement unit and its colour.
 */
export function buildSeriesCatalogue(
  baseConfig: UiConfig,
  allProperties: ConfigurableProperty[],
  translations: DigitalTwinTranslations,
  overrideAlias: UiConfigAliases
): AppliedSettingSeries[] {
  const aliases = getAliasesForAliasExpansion(baseConfig, overrideAlias)
  const itemsConfig: UiConfig = {
    component: baseConfig.component,
    alias: {},
    props: { items: clone(baseConfig.props.items ?? []) },
    dataset_instructions: [],
  }
  const items = applyAliases(itemsConfig, aliases).props.items ?? []
  const palette = generallyApplyAliases<unknown[]>(clone(PALETTE_ALIASES), aliases).filter(
    (color): color is string => typeof color === 'string'
  )
  const objects = groupPropertiesByObject(allProperties)
  const joinedObjects = joinItemsToObjects(sortByBaseConfigOrder(items), objects, translations)
  const itemlessObjects = objects.filter(
    (object) => !joinedObjects.some(({ level, name }) => level === object.level && name === object.name)
  )

  return [
    ...joinedObjects.flatMap((object) => toObjectSeries(object, palette, translations)),
    ...itemlessObjects.flatMap((object) => toSettingsOnlySeries(object, palette, translations)),
  ]
}
